import { useState, useEffect } from "react";
import { useAxios } from "../hooks/useAxios";

interface SignupResponse {
  success: boolean;
  message: string;
  hashUrl?: string;
  alreadyRegistered?: boolean;
}

export function useHome() {
  const { axiosClient } = useAxios();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const [isSignupComplete, setIsSignupComplete] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  // Header data states
  const [isOpen, setIsOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [date, setDate] = useState(""); // 表示用（MM/DD/YYYY）
  const [rawDate, setRawDate] = useState(""); // APIからの生値
  const [headerLoading, setHeaderLoading] = useState(true);
  const [headerError, setHeaderError] = useState("");

  useEffect(() => {
    async function fetchHeaderData() {
      setHeaderLoading(true);
      setHeaderError("");
      try {
        // 承認済みリクエスト件数取得
        const approvedRes = await axiosClient.get(
          "/api/requests/approved/count"
        );
        setCount(approvedRes.data.count);
        // 他のヘッダー情報も必要ならここで取得
        const res = await axiosClient.get("/api/waiting/status");
        setIsOpen(res.data.isOpen);
        setRawDate(res.data.date);
        // YYYY-MM-DD → 'Month YYYY' で表示
        if (res.data.date && /^\d{4}-\d{2}-\d{2}$/.test(res.data.date)) {
          const dateObj = new Date(res.data.date);
          if (!isNaN(dateObj.getTime())) {
            const month = dateObj.toLocaleString("en-US", { month: "long" });
            const year = dateObj.getFullYear();
            setDate(`${month} ${year}`);
          } else {
            setDate("");
          }
        } else {
          setDate("");
        }
      } catch (err: any) {
        setHeaderError(err.message || "Failed to fetch header data");
      } finally {
        setHeaderLoading(false);
      }
    }
    fetchHeaderData();
  }, [axiosClient]);

  const handleSignup = async (email: string) => {
    setLoading(true);
    setMessage("");
    setTitle("");
    setError("");

    try {
      const response = await axiosClient.post<SignupResponse>(
        "/api/waiting/register",
        { email }
      );

      if (response.data.success) {
        if (isOpen) {
          if (!!response.data.alreadyRegistered) {
            setTitle("Registration complete");
            setMessage("The URL for the request form has been resent.");
          } else {
            setTitle("Registration complete");
            setMessage("I have sent the URL for the request form.");
          }
        } else {
          if (!!response.data.alreadyRegistered) {
            setTitle("Registration complete");
            setMessage("Notification registration has already been completed.");
          } else {
            setTitle("Registration complete");
            setMessage("A confirmation email has been sent.");
          }
        }
        setAlreadyRegistered(!!response.data.alreadyRegistered);
        setIsSignupComplete(true);
      } else {
        setError("登録に失敗しました。もう一度お試しください。");
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "登録中にエラーが発生しました";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    title,
    message,
    setMessage,
    error,
    setError,
    isSignupComplete,
    setIsSignupComplete,
    alreadyRegistered,
    setAlreadyRegistered,
    handleSignup,
    isOpen,
    count,
    date, // 表示用 MM/DD/YYYY
    rawDate, // API生値 YYYY-MM-DD
    headerLoading,
    headerError,
  };
}
